import type { DeploymentProcessResource, GitRef, GitRefResource, ProjectTemplateDetailsResource, Repository, ValidateGitRefV2Response } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import { PageHeaderBranchSelector } from "~/areas/projects/components/BranchSelector/PageHeaderBranchSelector";
import useDeploymentProcessState from "~/areas/projects/components/Process/useDeploymentProcessState";
import { VcsError } from "~/areas/projects/components/VersionControl/VcsError";
import type { ProjectContextProps } from "~/areas/projects/context/index";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { PageContent } from "~/components/PageContent/PageContent";
import ProcessListLayoutDataLoader from "./ProcessListLayoutLoader";
export async function deploymentProcessLoader(repository: Repository, projectContext: Promise<ProjectContextProps>, gitRef: string | undefined): Promise<DeploymentProcessLoaderData> {
    const { state: { model: project }, } = await projectContext;
    return getDeploymentProcess(repository, project.SpaceId, project.Id, gitRef, project.ProjectTemplateDetails);
}
export type DeploymentProcessLoaderData = DataWithValidProcess | DataWithFailedGitRefValidationResult;
export type DataWithValidProcess = {
    process: DeploymentProcessResource;
    gitRefValidationResult: ValidateGitRefV2Response | undefined;
};
type DataWithFailedGitRefValidationResult = {
    gitRefValidationResult: ValidateGitRefV2Response;
};
interface DeploymentProcessPageQueryParams {
    newlyCreatedProject: boolean | undefined;
    configureProject: boolean | undefined;
    loadSampleProcess: string | undefined;
}
export interface DeploymentProcessPageProps {
    loaderData: DeploymentProcessLoaderData;
    projectContext: ProjectContextProps;
    queryParams: DeploymentProcessPageQueryParams;
    gitRef: string | undefined;
}
export function DeploymentProcessPage({ loaderData, projectContext, queryParams, gitRef }: DeploymentProcessPageProps) {
    const { model: project } = projectContext.state;
    const { deploymentProcessIdentifier, deploymentProcessData, reloadDeploymentProcess, modifyDeploymentProcess } = useDeploymentProcessState(project, loaderData, gitRef);
    const spaceNavigation = useSpaceAwareNavigation();
    const navigateToGitRef = (gitRef: string) => spaceNavigation.navigate(links.branchDeploymentProcessPage.generateUrl({ spaceId: project.SpaceId, projectSlug: project.Slug, branchName: gitRef }, queryParams));
    if (isDataWithInvalidGitRef(deploymentProcessData)) {
        return (<PageContent header={{ title: "Process" }}>
                <VcsError project={project} error={deploymentProcessData.gitRefValidationResult} gitRef={gitRef} setGitRef={navigateToGitRef}/>
            </PageContent>);
    }
    const onProcessSave = (gitRef: GitRefResource | string | undefined) => projectContext.actions.onProjectUpdated(project, gitRef);
    const refreshModel = (gitRef?: GitRef) => projectContext.actions.refreshModel(gitRef);
    return (<ProcessListLayoutDataLoader project={project} onProcessSave={onProcessSave} refreshModel={refreshModel} gitRef={gitRef} gitRefResource={deploymentProcessData.gitRefValidationResult?.GitRef} processIdentifier={deploymentProcessIdentifier} process={deploymentProcessData.process} reloadProcess={reloadDeploymentProcess} modifyProcess={modifyDeploymentProcess} releaseCreationPackageStepId={project.ReleaseCreationStrategy.ReleaseCreationPackageStepId} titleAccessory={<PageHeaderBranchSelector project={project} gitRef={gitRef} setGitRef={navigateToGitRef}/>}/>);
}
export function isDataWithInvalidGitRef(data: DeploymentProcessLoaderData): data is DataWithFailedGitRefValidationResult {
    return data.gitRefValidationResult !== undefined && data.gitRefValidationResult.Type !== "Success";
}
export async function getDeploymentProcess(repository: Repository, spaceId: string, projectId: string, gitRef: string | undefined, projectTemplateDetails: ProjectTemplateDetailsResource | null): Promise<DeploymentProcessLoaderData> {
    let gitRefValidationResult: ValidateGitRefV2Response | undefined = undefined;
    if (gitRef) {
        gitRefValidationResult = await repository.Projects.validateGitRef(spaceId, projectId, gitRef);
        if (gitRefValidationResult.Type !== "Success") {
            return {
                gitRefValidationResult,
            };
        }
    }
    if (projectTemplateDetails) {
        const deploymentProcess = await repository.TemplatedProjects.getTemplatedProjectDeploymentProcess(projectId);
        return {
            process: deploymentProcess,
            gitRefValidationResult,
        };
    }
    const deploymentProcess = await repository.DeploymentProcesses.get(projectId, gitRef);
    return {
        process: deploymentProcess,
        gitRefValidationResult,
    };
}
